// CommissionValidationPanel.tsx — May 2, 2026
//
// On-demand commission queue validation backstop. Mounts on Finance Board >
// Commissions tab when the role pill = Executive AND the user is a super user
// (greg / chad / jennifer / jcohan, or admin role). Click "Run Validation" →
// calls validate_commission_queue() RPC → renders summary + collapsible
// per-deal findings inline. If anything is BLOCKED, drops a notification on
// Chad's board so he can't miss it.
//
// Replaces the deprecated commission-pre-validator scheduled task (which wrote
// HTML reports nobody opened). On-demand beats nightly cron because:
//   - data is always fresh (no race between cron and edits)
//   - no findings table to maintain / expire
//   - executive review naturally pairs with "click button → see what's flagged"
//
// The 6 checks are documented in the migration comment for
// validate_commission_queue() — see 20260502_validate_commission_queue.

import { useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import DealLink from "../DealLink";
import { fmtUSD } from "../../lib/analytics";

const CHAD_EMAIL = "chad@bardinroofing.com";

type Severity = "blocked" | "warning" | "clean";

interface CheckResult {
  id: string;
  label: string;
  severity: Severity;
  detail: string;
  computed?: number;
  stored?: number;
  skipped?: boolean;
}

interface DealResult {
  deal_id: string;
  homeowner_name: string | null;
  claim_number: string | null;
  stage: string;
  rm_name: string | null;
  rm_role: string | null;
  rm_tier_pct: number | null;
  split_rm_name: string | null;
  computed_commissionable: number | null;
  stored_commissionable: number | null;
  scope_inputs_empty: boolean;
  severity: Severity;
  checks: CheckResult[];
  build_completed_date: string | null;
}

interface ValidateResult {
  summary: {
    total: number;
    blocked: number;
    warning: number;
    clean: number;
    ran_at: string;
  };
  deals: DealResult[];
}

export default function CommissionValidationPanel() {
  const { member } = useAuth();
  const [result, setResult] = useState<ValidateResult | null>(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<Severity | "all">("all");
  const [chadNotified, setChadNotified] = useState(false);

  async function runValidation() {
    setRunning(true);
    setErr(null);
    setChadNotified(false);
    try {
      const { data, error } = await supabase.rpc("validate_commission_queue");
      if (error) {
        setErr(error.message);
        setResult(null);
        return;
      }
      const res = data as unknown as ValidateResult;
      setResult(res);
      // Auto-open every flagged row so issues are visible without extra clicks.
      // Clean rows stay collapsed.
      setExpanded(
        new Set(res.deals.filter((d) => d.severity !== "clean").map((d) => d.deal_id)),
      );
      // If anything BLOCKED, fire-and-forget a notification to Chad. Don't
      // await — user gets the result immediately, notification lands behind
      // the scenes.
      if (res.summary.blocked > 0) {
        notifyChad(res, member?.full_name ?? "Executive")
          .then(() => setChadNotified(true))
          .catch((e) => console.warn("[CommissionValidationPanel] Chad notify failed:", e));
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Unknown error");
    } finally {
      setRunning(false);
    }
  }

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const visibleDeals =
    result?.deals.filter((d) => filter === "all" || d.severity === filter) ?? [];

  return (
    <div className="bg-white border-2 border-bardin-orange/30 rounded-xl p-4 mb-5 shadow-sm">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-bardin-ink">
            🔍 Commission Queue Validation
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Independent backstop — recalc · tier · split · misc credit · overrides ·
            duplicate-payment. Latest 200 unpaid built deals.
          </p>
        </div>
        <button
          onClick={runValidation}
          disabled={running}
          className="px-4 py-2 bg-bardin-orange text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition disabled:opacity-60 flex-shrink-0"
        >
          {running ? "Running…" : result ? "Re-run Validation" : "Run Validation"}
        </button>
      </div>

      {err && (
        <div className="mt-3 bg-red-50 text-red-700 p-3 rounded text-sm border border-red-200">
          Validation failed — {err}
        </div>
      )}

      {result && (
        <>
          {/* Summary tiles — clickable to filter */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
            <SummaryTile
              label="Total in queue"
              count={result.summary.total}
              cls="bg-slate-50 text-slate-700 border-slate-200"
              active={filter === "all"}
              onClick={() => setFilter("all")}
            />
            <SummaryTile
              label="🔴 Blocked"
              count={result.summary.blocked}
              cls="bg-red-50 text-red-700 border-red-300"
              active={filter === "blocked"}
              onClick={() => setFilter("blocked")}
            />
            <SummaryTile
              label="🟡 Warnings"
              count={result.summary.warning}
              cls="bg-amber-50 text-amber-700 border-amber-300"
              active={filter === "warning"}
              onClick={() => setFilter("warning")}
            />
            <SummaryTile
              label="✅ Clean"
              count={result.summary.clean}
              cls="bg-emerald-50 text-emerald-700 border-emerald-300"
              active={filter === "clean"}
              onClick={() => setFilter("clean")}
            />
          </div>

          <div className="text-[11px] text-slate-400 mt-2 flex items-center justify-between flex-wrap gap-2">
            <span>
              Ran at {new Date(result.summary.ran_at).toLocaleString()} · showing{" "}
              {visibleDeals.length} of {result.summary.total}
              {filter !== "all" && (
                <button
                  onClick={() => setFilter("all")}
                  className="ml-2 text-bardin-orange underline hover:no-underline"
                >
                  show all
                </button>
              )}
            </span>
            {chadNotified && (
              <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
                ✉ Chad notified of blocked items
              </span>
            )}
          </div>

          {/* Findings list */}
          <div className="mt-3 space-y-1.5">
            {visibleDeals.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">
                {filter === "blocked"
                  ? "🎉 No blocked deals — safe to pay the queue."
                  : filter === "warning"
                    ? "🎉 No warnings."
                    : "Nothing in this view."}
              </div>
            ) : (
              visibleDeals.map((d) => (
                <DealRow
                  key={d.deal_id}
                  deal={d}
                  expanded={expanded.has(d.deal_id)}
                  onToggle={() => toggleExpand(d.deal_id)}
                />
              ))
            )}
          </div>
        </>
      )}

      {!result && !err && !running && (
        <div className="mt-4 text-center py-6 text-sm text-slate-500 border-t border-slate-100">
          Click <strong>Run Validation</strong> to sweep the commission queue. Validates
          the latest 200 deals where build is complete and commission is unpaid.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SummaryTile({
  label,
  count,
  cls,
  active,
  onClick,
}: {
  label: string;
  count: number;
  cls: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "text-left rounded-xl border px-3 py-2 transition " +
        cls +
        " " +
        (active ? "ring-2 ring-bardin-orange shadow-sm" : "hover:shadow-sm")
      }
    >
      <div className="text-[10px] font-bold uppercase tracking-wide opacity-80">
        {label}
      </div>
      <div className="text-2xl font-extrabold mt-1">{count}</div>
    </button>
  );
}

function DealRow({
  deal,
  expanded,
  onToggle,
}: {
  deal: DealResult;
  expanded: boolean;
  onToggle: () => void;
}) {
  const sev = deal.severity;
  const sevColor =
    sev === "blocked"
      ? "border-red-400 bg-red-50/40"
      : sev === "warning"
        ? "border-amber-400 bg-amber-50/40"
        : "border-emerald-300 bg-emerald-50/40";
  const sevIcon = sev === "blocked" ? "🔴" : sev === "warning" ? "🟡" : "✅";
  const failedChecks = deal.checks.filter((c) => c.severity !== "clean");

  return (
    <div className={"border rounded-lg " + sevColor}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-white/40"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="flex-shrink-0">{sevIcon}</span>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm text-bardin-ink truncate">
              <DealLink id={deal.deal_id}>{deal.homeowner_name || "(no name)"}</DealLink>
              {deal.claim_number && (
                <span className="text-xs text-slate-500 ml-2">#{deal.claim_number}</span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {deal.rm_name || "?"} · {deal.stage}
              {failedChecks.length > 0 && (
                <span className="ml-1">
                  · {failedChecks.length} flag{failedChecks.length === 1 ? "" : "s"}:{" "}
                  {failedChecks.map((c) => c.label).join(", ")}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right">
            <div className="text-sm font-semibold text-bardin-ink">
              {fmtUSD(Number(deal.stored_commissionable) || 0, { short: true })}
            </div>
            <div className="text-[10px] text-slate-500">stored</div>
          </div>
          <span className="text-slate-400 text-xs">{expanded ? "▾" : "▸"}</span>
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-1.5 border-t border-slate-200/60 bg-white/60">
          {deal.checks.map((c) => (
            <CheckLine key={c.id} check={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function CheckLine({ check }: { check: CheckResult }) {
  const cls =
    check.severity === "blocked"
      ? "bg-red-100 text-red-800"
      : check.severity === "warning"
        ? "bg-amber-100 text-amber-800"
        : "bg-emerald-100 text-emerald-800";
  const icon =
    check.severity === "blocked" ? "🔴" : check.severity === "warning" ? "🟡" : "✅";
  return (
    <div className="flex items-start gap-2 text-xs">
      <span
        className={
          "px-1.5 py-0.5 rounded font-semibold flex-shrink-0 whitespace-nowrap " + cls
        }
      >
        {icon} {check.label}
      </span>
      <span className="text-slate-700 pt-0.5 leading-relaxed">{check.detail}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chad notification helper — fires when validation finds BLOCKED items.
// Mirrors the notifyChadOnBuildApproval pattern (lookup by email →
// in-app notification). No SMS for this one — validation is something the
// user already sat down to do; surfacing on his board is enough.
// ---------------------------------------------------------------------------

async function notifyChad(result: ValidateResult, runByName: string): Promise<void> {
  const { data: chad, error } = await supabase
    .from("team_roster")
    .select("id")
    .eq("email", CHAD_EMAIL)
    .eq("is_active", true)
    .maybeSingle();
  if (error || !chad) {
    console.warn("[CommissionValidationPanel] Chad lookup failed:", error?.message);
    return;
  }

  const blocked = result.deals.filter((d) => d.severity === "blocked");
  const sample = blocked.slice(0, 5);
  const lines = sample
    .map(
      (d) =>
        `• ${d.homeowner_name || "(no name)"} — ${d.checks
          .filter((c) => c.severity === "blocked")
          .map((c) => c.label)
          .join(", ")}`,
    )
    .join("\n");
  const more =
    result.summary.blocked > sample.length
      ? `\n…and ${result.summary.blocked - sample.length} more`
      : "";

  await supabase.from("notifications").insert({
    user_id: chad.id,
    type: "commission_validation_blocked",
    title: `🚨 Commission validation: ${result.summary.blocked} blocked`,
    body: `Run by ${runByName}.\n${lines}${more}\n\nReview before approving any commissions.`,
    metadata: {
      ran_at: result.summary.ran_at,
      blocked_count: result.summary.blocked,
      blocked_deal_ids: blocked.map((d) => d.deal_id),
    },
  });
}
